/**
 * VideoThumbnail Component
 * Individual video thumbnail card with play overlay and delete button
 */

import { useState, useEffect } from 'react';
import { activate } from "../../../../../utils/keyboard-activate";
import { getValidatedGcsUrl } from '../../../../../utils/url-validation';
import { isClip } from '../../../../../types/prospect-media';
import type { ProspectVideo } from '../../../../../types/prospect-media';
import './VideoThumbnail.css';

interface VideoThumbnailProps {
  video: ProspectVideo;
  onClick: () => void;
  onDelete: () => void;
  getThumbnailUrl: (videoId: string) => Promise<string | null>;
  /** Omitted for clips — clipping a clip isn't supported. */
  onClip?: () => void;
}

export function VideoThumbnail({
  video,
  onClick,
  onDelete,
  getThumbnailUrl,
  onClip,
}: VideoThumbnailProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const videoIsClip = isClip(video);

  useEffect(() => {
    const loadThumbnail = async () => {
      try {
        const url = await getThumbnailUrl(video.id);

        // Validate URL before setting
        const validatedUrl = getValidatedGcsUrl(url);
        if (!validatedUrl) {
          console.error('[VideoThumbnail] Invalid or non-GCS thumbnail URL', {
            videoId: video.id,
            receivedUrl: url,
          });
          setError(true);
        } else {
          setThumbnailUrl(validatedUrl);
          setError(false);
        }
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadThumbnail();
  }, [video.id, getThumbnailUrl]);

  const formatDuration = (seconds?: number | null): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // A clip's poster frame is captured client-side and may legitimately be missing (e.g. the
  // browser refused to read pixels from the cross-origin source). Showing "Failed to load" for
  // a perfectly playable clip reads as breakage, so clips get a placeholder tile instead.
  const showPlaceholder = !isLoading && (error || !thumbnailUrl) && videoIsClip;

  return (
    <div className="video-thumbnail" {...activate(onClick)}>
      {isLoading && (
        <div className="video-thumbnail__loading">Loading...</div>
      )}
      {error && !isLoading && !videoIsClip && (
        <div className="video-thumbnail__error">Failed to load</div>
      )}
      {((thumbnailUrl && !error) || showPlaceholder) && !isLoading && (
        <>
          {showPlaceholder ? (
            <div className="video-thumbnail__placeholder" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M7 4v16M17 4v16M2 12h20" />
              </svg>
            </div>
          ) : (
            <img
              className="video-thumbnail__image"
              src={thumbnailUrl!}
              alt={video.title ? `Thumbnail for ${video.title}` : 'Video thumbnail'}
              loading="lazy"
            />
          )}
          <div className="video-thumbnail__play-overlay">
            <div className="video-thumbnail__play-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
          {videoIsClip && <div className="video-thumbnail__clip-badge">Clip</div>}
          {video.durationSeconds && (
            <div className="video-thumbnail__duration">
              {formatDuration(video.durationSeconds)}
            </div>
          )}
          <div className="video-thumbnail__overlay">
            {video.title && <p className="video-thumbnail__name">{video.title}</p>}
            <p className="video-thumbnail__date">{formatDate(video.createdAt)}</p>
            {video.uploadedByName && (
              <p className="video-thumbnail__uploader">{video.uploadedByName}</p>
            )}
          </div>
        </>
      )}
      <div className="video-thumbnail__actions">
        {onClip && (
          <button
            className="video-thumbnail__clip"
            onClick={(e) => {
              e.stopPropagation();
              onClip();
            }}
            aria-label="Clip video"
            title="Clip this video"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="6" cy="6" r="3" />
              <circle cx="6" cy="18" r="3" />
              <path d="M20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12" />
            </svg>
          </button>
        )}
        <button
          className="video-thumbnail__delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete video"
        >
          ×
        </button>
      </div>
    </div>
  );
}
